<script setup lang="ts">
import { onMounted, ref } from 'vue'
import ManagementResource from '@/api/resources/management'
import ConfirmationModal from '@/components/Globals/ConfirmationModal.vue'
import TariffFormModal from '@/components/Management/Tariffs/TariffFormModal.vue'
import type { IEnergyTariff } from '@/interfaces/entities/management'
import { useErrorStore } from '@/store/error'
import { useManagementStore } from '@/store/management'

const tariffs = ref<IEnergyTariff[]>([])
const isFormOpen = ref(false)
const editingTariff = ref<IEnergyTariff | null>(null)
const isDeleteOpen = ref(false)
const tariffToDelete = ref<IEnergyTariff | null>(null)

const money = (value: number) => `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
const formatDate = (iso: string) => new Date(iso).toLocaleDateString('pt-BR')

async function fetchTariffs() {
  try {
    const res = await ManagementResource.listTariffs()
    tariffs.value = Array.isArray(res) ? res : (res?.data ?? [])
  } catch {
    tariffs.value = []
  }
}

function openCreate() {
  editingTariff.value = null
  isFormOpen.value = true
}

function openEdit(tariff: IEnergyTariff) {
  editingTariff.value = tariff
  isFormOpen.value = true
}

async function save(payload: Record<string, unknown>) {
  try {
    if (editingTariff.value) {
      await ManagementResource.updateTariff(editingTariff.value.id, payload)
    } else {
      await ManagementResource.createTariff(payload)
    }
    isFormOpen.value = false
    await fetchTariffs()
    // Tarifa mudou → invalida o cache de indicadores para recalcular o custo.
    useManagementStore().clearIndicators()
  } catch (e: any) {
    useErrorStore().setErrors(e?.response?.data?.errors ?? ['Não foi possível salvar a tarifa.'])
  }
}

function confirmDelete(tariff: IEnergyTariff) {
  tariffToDelete.value = tariff
  isDeleteOpen.value = true
}

async function executeDelete() {
  if (!tariffToDelete.value) return
  try {
    await ManagementResource.deleteTariff(tariffToDelete.value.id)
    tariffs.value = tariffs.value.filter((t) => t.id !== tariffToDelete.value!.id)
    useManagementStore().clearIndicators()
  } catch {
    useErrorStore().setErrors(['Não foi possível excluir a tarifa.'])
  } finally {
    isDeleteOpen.value = false
    tariffToDelete.value = null
  }
}

onMounted(fetchTariffs)
</script>

<template>
  <section class="flex flex-col gap-3">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-base font-bold text-slate-900">Tarifas de energia</h3>
        <p class="text-sm text-slate-500">Base para o cálculo de custo de energia no Módulo Gerencial.</p>
      </div>
      <button
        type="button"
        class="border border-primary bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary/90"
        @click="openCreate">
        Nova tarifa
      </button>
    </div>

    <div class="overflow-x-auto border border-stroke bg-white">
      <table class="w-full text-left text-sm">
        <thead class="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th class="px-4 py-3">Nome</th>
            <th class="px-4 py-3 text-right">Consumo ponta</th>
            <th class="px-4 py-3 text-right">Consumo fora ponta</th>
            <th class="px-4 py-3 text-right">Demanda ponta</th>
            <th class="px-4 py-3 text-right">Demanda fora ponta</th>
            <th class="px-4 py-3">Vigência</th>
            <th class="px-4 py-3 text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="tariff in tariffs" :key="tariff.id" class="border-t border-stroke">
            <td class="px-4 py-3 font-medium text-slate-800">{{ tariff.name }}</td>
            <td class="px-4 py-3 text-right">{{ money(tariff.rate_peak_consumption) }}</td>
            <td class="px-4 py-3 text-right">{{ money(tariff.rate_offpeak_consumption) }}</td>
            <td class="px-4 py-3 text-right">{{ money(tariff.rate_peak_demand) }}</td>
            <td class="px-4 py-3 text-right">{{ money(tariff.rate_offpeak_demand) }}</td>
            <td class="px-4 py-3 text-slate-600">
              {{ formatDate(tariff.valid_from) }} — {{ tariff.valid_until ? formatDate(tariff.valid_until) : 'atual' }}
            </td>
            <td class="px-4 py-3">
              <div class="flex justify-end gap-2">
                <button
                  type="button"
                  class="border border-stroke px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  @click="openEdit(tariff)">
                  Editar
                </button>
                <button
                  type="button"
                  class="border border-red-200 px-3 py-1.5 text-xs font-medium text-red hover:bg-red-50"
                  @click="confirmDelete(tariff)">
                  Excluir
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="!tariffs.length">
            <td colspan="7" class="px-4 py-12 text-center text-slate-400">Nenhuma tarifa cadastrada.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <TariffFormModal :is-open="isFormOpen" :editing="editingTariff" @cancel="isFormOpen = false" @save="save" />

    <ConfirmationModal
      :is-open="isDeleteOpen"
      cancel-text="Cancelar"
      confirmation-text="Excluir"
      @cancel="isDeleteOpen = false"
      @add="executeDelete">
      <template #content>
        <div class="space-y-2">
          <h3 class="text-lg font-semibold text-slate-900">Excluir tarifa?</h3>
          <p class="text-sm text-slate-600">A tarifa <strong>{{ tariffToDelete?.name }}</strong> será removida.</p>
        </div>
      </template>
    </ConfirmationModal>
  </section>
</template>
